import { defineStore } from "pinia";

import { TeambuildingState } from "~/js/interfaces/teambuildings-tm";
import { Facet, Filter, SingleFilter } from "~/js/interfaces/facets";

import {
    useSearchParameters,
    useTypesenseClient,
} from "~/js/composables/typesense";

export const useTeambuildingState = defineStore("tm-teambuildings", {
    state: (): TeambuildingState => ({
        facets: [],
        filters: {
            searchQuery: "",
            filterQuery: `site:${window.language}`,
            categories: {
                field: "categories",
                values: [],
            },
            durations: {
                field: "durations",
                values: [],
            },
            locationTypes: {
                field: "location_types",
                values: [],
            },
            new: {
                field: "new",
                value: null,
            },
            online: {
                field: "online",
                value: null,
            },
            participants: {
                field: "participants",
                values: [],
            },
            priceRanges: {
                field: "price_ranges",
                values: [],
            },
            purposes: {
                field: "purposes",
                values: [],
            },
            seasonals: {
                field: "seasonals",
                values: [],
            },
            spotlights: {
                field: "spotlight",
                value: null,
            },
        },
        index: `teammasters_${
            import.meta.env.VITE_TYPESENSE_ENVIRONMENT
        }_teambuildings`,
        loading: true,
        pagination: {
            current: 0,
            totalPages: 0,
            totalResults: 0,
            foundResults: 0,
            itemsPerPage: 12,
        },
        settings: {
            showFilters: true,
            useQueryParams: true,
        },
        sorting: {
            type: "post_date_timestamp",
            order: "asc",
        },
        teambuildings: null,
    }),

    persist: {
        pick: [
            "filters.categories",
            "filters.durations",
            "filters.locationTypes",
            "filters.new",
            "filters.online",
            "filters.participants",
            "filters.priceRanges",
            "filters.purposes",
            "filters.seasonals",
            "filters.spotlights",
        ],
    },

    actions: {
        async fetch(
            this: typeof useTeambuildingState,
            reset = false,
            setQueryParams = false
        ): Promise<void> {
            this.loading = true;

            // set URL query parameters if existing
            if (setQueryParams) {
                this.setQueryFilters();
            } 
            
            this.setFiltersQuery();

            if (reset) {
                this.pagination.current = 0;
            } else {
                this.pagination.current += 1;
            }

            const client = useTypesenseClient();
            const searchParameters = useSearchParameters(
                this.filters,
                this.sorting
            );

            const determineFacets = () => {
                return ['categories', 'durations', 'location_types', 'participants', 'price_ranges', 'purposes', 'seasonals']
                    .filter(facet => this.filters[facet]?.values?.length > 0)
                    .join(',');
            }

            const searchRequests = {
                searches: [
                    {
                        collection: this.index,
                        facet_by: determineFacets,
                        max_facet_values: 100,
                        page: this.pagination.current,
                        per_page: this.pagination.itemsPerPage,
                        ...searchParameters,
                    },
                    {
                        collection: this.index,
                        max_facet_values: 100,
                        facet_by: "",
                        filter_by: `site:${window.language}`,
                        page: 1,
                        per_page: 1,
                        q: "*",
                        sort_by: "post_date_timestamp:asc",
                    },
                ],
            };

            await client.multiSearch
                .perform(searchRequests)
                .then((results: any) => {
                    // results
                    const filteredResults = results.results[0];
                    const hits = filteredResults?.hits || [];
                    if (reset) {
                        this.teambuildings = hits;
                    } else {
                        this.teambuildings = [
                            ...(this.teambuildings || []),
                            ...hits,
                        ];
                    }

                    // facets
                    const facets = filteredResults?.facet_counts || [];
                    this.facets = facets;

                    // update pagination
                    this.pagination.current = filteredResults.page;
                    this.pagination.totalPages = this.pagination.itemsPerPage
                        ? Math.ceil(filteredResults.found / this.pagination.itemsPerPage)
                        : 0;
                    this.pagination.foundResults = filteredResults.found;
                    this.pagination.totalResults = filteredResults.out_of;

                    setTimeout(() => {
                        this.loading = false;
                    }, 1000);
                });
        },

        setQueryFilters(this: typeof useTeambuildingState): void {
            if (!window.location.search) return;

            const params = new URLSearchParams(window.location.search);

            // empty values
            this.resetFilters(false, true);

            // add query string parameters to the filters
            params.forEach((value, key) => {
                // Check if this key exists as a filter property
                if (this.filters[key]) {
                    const filter = this.filters[key];
                    
                    // Check if it has a values array (multiple selection)
                    if ('values' in filter && Array.isArray(filter.values)) {
                        const numericValue = Number(value);
                        
                        if (isNaN(numericValue)) {
                            console.warn(`Skipping non-numeric value for ${key}: ${value}`);
                            return;
                        }
                        
                        filter.values.push(numericValue);
                    } 
                    // Check if it has a single value property
                    else if ('value' in filter) {
                        filter.value = value;
                    }
                }

                if (key == "filters") {
                    this.settings.showFilters = value == "false" ? false : true;
                }
            });
        },

        setUrlString(this: typeof useTeambuildingState): void {
            const setQueryStringsFromFilters = [
                "categories",
                "purposes", 
                "durations",
                "locationTypes",
                "participants",
                "priceRanges",
                "seasonals",
            ];

            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            
            // Reset query strings
            setQueryStringsFromFilters.forEach((type: string) => {
                params.delete(type);
            });

            // Set query string based on values
            setQueryStringsFromFilters.forEach((type: string) => {
                const filter: Filter | SingleFilter = this.filters?.[type];
                
                if (!filter) return;

                if ("values" in filter && filter.values.length > 0) {
                    filter.values.forEach((value: any) => {
                        params.append(type, String(value));
                    });
                }

                if ("value" in filter && filter.value) {
                    params.set(type, String(filter.value));
                }
            });

            // Update filters parameter (ensure it's a string)
            params.set("filters", String(this.settings.showFilters));

            // Apply the new query string
            url.search = params.toString();
            history.pushState({}, "", url.toString());
        },

        resetFilters(
            this: typeof useTeambuildingState,
            fetch = true,
            resetAllFilters = this.settings.showFilters
        ): void {
            let facets = [
                "categories",
                "purposes",
                "durations",
                "locationTypes",
                "participants",
                "priceRanges",
                "seasonals",
            ];
            const properties = ["online", "new", "spotlights"];

            if (!resetAllFilters) {
                facets = facets.filter(
                    (value: string) =>
                        value != "categories" && value != "purposes"
                );
            }

            facets.forEach((facet) => {
                this.filters[facet].values = [];
            });

            properties.forEach((property) => {
                this.filters[property].value = null;
            });

            this.filters.searchQuery = "";

            this.setFiltersQuery();

            if (fetch) {
                this.fetch(true);
            }
        },

        setSearchFunnel(
            this: typeof useTeambuildingState,
            filters: Array<Filter | SingleFilter>,
            redirectUrl: string
        ): void {
            // reset filters to start from a blank slate
            this.resetFilters(false);

            // set the filters to hide on overview
            this.settings.showFilters = false;

            // set the search funnel filters
            filters.forEach((set) => {
                const filter: Filter | SingleFilter =
                    this.filters[set.field] ?? null;

                if ("value" in filter && "value" in set) {
                    filter.value = set.value;
                }

                if ("values" in filter && "values" in set) {
                    filter.values = set.values;
                }
            });

            // set query filters
            this.setFiltersQuery();

            console.table(filters);

            // redirect after setting the funnel
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        },

        setFiltersQuery(this: typeof useTeambuildingState): void {
            this.filters.filterQuery = "";

            /* ----------------------------------------
             ** BUILD THE CATEGORY / PURPOSE FILTERS
             ** ----------------------------------------*/
            // check if category or purpose has a value
            const filters = ["categories", "purposes"];
            const appliedFilters: Array<Facet> = [];
            filters.forEach((filter) => {
                if (this.filters[filter].values.length > 0) {
                    appliedFilters.push(this.filters[filter]);
                }
            });

            // provide a colon for grouping the buildup
            if (appliedFilters.length > 0) {
                this.filters.filterQuery += "(";
            }

            // loop through the applied filters to set the grouped categories
            for (let i = 0; i < appliedFilters.length; i++) {
                const facet = appliedFilters[i];

                this.filters.filterQuery +=
                    facet.field + ":[" + facet.values.toString() + "]";

                if (
                    appliedFilters.length > 0 &&
                    i != appliedFilters.length - 1
                ) {
                    this.filters.filterQuery += " || ";
                }
            }

            // provide a colon for grouping the buildup
            if (appliedFilters.length > 0) {
                this.filters.filterQuery += ")";
            }

            // start combination of the facets, if buildup is set
            if (this.filters.filterQuery.length > 0) {
                this.filters.filterQuery += ` && `;
            }

            /* ----------------------------------------
             ** BUILD THE FACET FILTERS
             ** ----------------------------------------*/
            const facets = [
                "durations",
                "locationTypes",
                "participants",
                "priceRanges",
                "seasonals",
            ];
            const appliedFacets: Array<Facet> = [];
            facets.forEach((filter) => {
                if (this.filters[filter].values.length > 0) {
                    appliedFacets.push(this.filters[filter]);
                }
            });

            // provide a colon for grouping the buildup
            if (appliedFacets.length > 0) {
                this.filters.filterQuery += "(";
            }

            // loop through the applied filters to set the grouped categories
            for (let i = 0; i < appliedFacets.length; i++) {
                const facet = appliedFacets[i];

                this.filters.filterQuery +=
                    facet.field + ":[" + facet.values.toString() + "]";

                if (appliedFacets.length > 0 && i != appliedFacets.length - 1) {
                    this.filters.filterQuery += " && ";
                }
            }

            // provide a colon for grouping the buildup
            if (appliedFacets.length > 0) {
                this.filters.filterQuery += ")";
            }

            // start combination of the facets, if buildup is set
            if (
                this.filters.filterQuery.length > 0 &&
                appliedFacets.length > 0
            ) {
                this.filters.filterQuery += ` && `;
            }

            /* ----------------------------------------
             ** BUILD THE BOOLEAN FILTERS
             ** ----------------------------------------*/
            const properties = ["online", "spotlights", "new"];
            const appliedProperties: Array<Facet> = [];
            properties.forEach((filter) => {
                if (this.filters[filter].value != null) {
                    appliedProperties.push(this.filters[filter]);
                }
            });

            // loop through the applied filters to set the grouped categories
            for (let i = 0; i < appliedProperties.length; i++) {
                const property = appliedProperties[i];

                this.filters.filterQuery +=
                    property.field + ":" + property.value + "";

                if (
                    appliedProperties.length > 0 &&
                    i != appliedProperties.length - 1
                ) {
                    this.filters.filterQuery += " && ";
                }
            }

            /* ----------------------------------------
             ** SANITISE
             ** ----------------------------------------*/
            // remove empty ' && '
            const ampersandCombination = " && ";
            if (
                this.filters.filterQuery.lastIndexOf(ampersandCombination) >=
                this.filters.filterQuery.length - 12
            ) {
                this.filters.filterQuery = this.filters.filterQuery.substring(
                    0,
                    this.filters.filterQuery.lastIndexOf(ampersandCombination)
                );
            }

            // remove empty ' || '
            const pipesCombination = " || ";
            if (
                this.filters.filterQuery.lastIndexOf(pipesCombination) >=
                this.filters.filterQuery.length - 12
            ) {
                this.filters.filterQuery = this.filters.filterQuery.substring(
                    0,
                    this.filters.filterQuery.lastIndexOf(pipesCombination)
                );
            }

            /* ----------------------------------------
             ** ADD LANGUAGE
             ** ----------------------------------------*/
            if (this.filters.filterQuery.length > 0) {
                this.filters.filterQuery += ` && `;
            }
            this.filters.filterQuery += `site:${window.language}`;

            this.setUrlString();
        },
    },
});
